/**
 * Automation accounts feature — Zod schemas.
 *
 * Wire contract with backend/pkg/api/admin/automation_account.go.
 *
 * A automation account is a non-interactive admin identity with its
 * own admin_api_keys row (prefix `pureid_auto_`). Every field here
 * mirrors AutomationAccountResponse / CreateAutomationAccountRequest /
 * UpdateAutomationAccountRequest / RotateAutomationAccountRequest. If
 * the backend DTO changes, update this file in lockstep.
 */

import { principalRefSchema } from "@pureid/auth-core";
import { z } from "zod";

// RBAC roles a automation account can carry. Matches rbac.Role on
// the backend; the service layer enforces rank (SA cannot
// carry a role stricter than the calling admin's).
export const automationAccountRoleSchema = z.enum([
  "platform_admin",
  "platform_operator",
  "tenant_admin",
  "tenant_operator",
  "tenant_readonly",
]);
export type AutomationAccountRole = z.infer<typeof automationAccountRoleSchema>;

// Status values admin_users.status can take for kind='automation_account'.
// Schema CHECK chk_admin_users_sa_status narrows to just these two.
export const automationAccountStatusSchema = z.enum(["active", "inactive"]);
export type AutomationAccountStatus = z.infer<typeof automationAccountStatusSchema>;

// Row shape returned by list/get/create/rotate. `token` +
// `token_id` + `token_prefix` are ONLY populated on create /
// rotate responses — reads leave them empty. The Zod schema
// accepts them as optional so one shape covers every path.
export const automationAccountSchema = z.object({
  id: z.string(),
  tenant_id: z.string().optional(),
  name: z.string(),
  description: z.string().optional(),
  role: automationAccountRoleSchema,
  status: automationAccountStatusSchema,
  owner_admin_id: z.string().optional(),
  // Resolved, typed owner (the human admin who owns/created this SA) — the canonical shared
  // principal shape (@pureid/auth-core). Present only on the single-GET detail read; omitted on
  // the list and on a transient resolve fault (UI falls back to owner_admin_id).
  owner: principalRefSchema.optional(),
  expires_at: z.string(),
  last_login_at: z.string().optional(),
  created_at: z.string(),
  deactivated_at: z.string().optional(),
  // Write-only — only on create/rotate responses.
  token: z.string().optional(),
  token_prefix: z.string().optional(),
  token_id: z.string().optional(),
  // Populated ONLY on rotate when grace_seconds > 0. Wall-clock
  // deadline for the prior token's revocation; drives the
  // reveal panel's "old token valid until X" copy so the
  // operator knows exactly how long they have to swap.
  grace_expires_at: z.string().optional(),
});
export type AutomationAccount = z.infer<typeof automationAccountSchema>;

// Pagination envelope. Mirrors pkg/api/admin.ListSAPagination.
export const automationAccountListSchema = z.object({
  data: z.array(automationAccountSchema),
  pagination: z.object({
    total: z.number().int().nonnegative(),
    offset: z.number().int().nonnegative(),
    limit: z.number().int().nonnegative(),
  }),
});
export type AutomationAccountList = z.infer<typeof automationAccountListSchema>;

// List search — URL-driven filter state. `show_inactive` flips
// the list between "active only" (the default, matches what most
// operators want) and "all" (including soft-deleted rows kept
// for audit continuity). Coerced so the URL can encode it as
// "true"/"false" strings and survive copy-paste.
export const automationAccountsListSearchSchema = z.object({
  show_inactive: z.coerce.boolean().optional(),
});
export type AutomationAccountsListSearch = z.infer<
  typeof automationAccountsListSearchSchema
>;

// Create form input. `expires_in_days` is required by the form
// even though the backend treats 0 as "default" — surfacing an
// explicit number keeps operators honest about rotation cadence.
// Max 365 days matches the server-side cap; min 1 day matches
// the server-side minimum.
export const createAutomationAccountSchema = z.object({
  name: z
    .string()
    .min(3, "Name must be at least 3 characters")
    .max(40, "Name must be 40 characters or fewer")
    .regex(
      /^[a-z0-9][a-z0-9-]*[a-z0-9]$/,
      "Lowercase letters, digits, and hyphens only (not starting or ending with a hyphen)",
    ),
  description: z
    .string()
    .max(512, "Description must be 512 characters or fewer")
    .optional(),
  role: automationAccountRoleSchema,
  expires_in_days: z
    .number()
    .int()
    .min(1, "Expiry must be at least 1 day")
    .max(365, "Expiry cannot exceed 365 days"),
});
export type CreateAutomationAccountInput = z.infer<typeof createAutomationAccountSchema>;

// Rotate form input. Grace window measured in seconds per the
// backend's RotateAutomationAccountRequest. Zero means "revoke the
// old key immediately" (security-sensitive default).
export const rotateAutomationAccountSchema = z.object({
  grace_seconds: z
    .number()
    .int()
    .min(0, "Grace window cannot be negative")
    .max(24 * 3600, "Grace window cannot exceed 24 hours"),
});
export type RotateAutomationAccountInput = z.infer<typeof rotateAutomationAccountSchema>;

// Update (PATCH) input. Every field is optional — the server
// applies only the fields present in the request body.
//   - name / description: free-text edits (same validation as
//     create).
//   - extend_expires_in_days: BUMPS expires_at to now + N days
//     (NOT set-to-absolute-date); matches the backend's rotation-
//     hygiene primitive.
// The refine gates an empty payload on the client so we don't
// issue a no-op network request.
export const updateAutomationAccountSchema = z
  .object({
    name: z
      .string()
      .min(3, "Name must be at least 3 characters")
      .max(40, "Name must be 40 characters or fewer")
      .regex(
        /^[a-z0-9][a-z0-9-]*[a-z0-9]$/,
        "Lowercase letters, digits, and hyphens only (not starting or ending with a hyphen)",
      )
      .optional(),
    description: z
      .string()
      .max(512, "Description must be 512 characters or fewer")
      .optional(),
    extend_expires_in_days: z
      .number()
      .int()
      .min(1, "Extension must be at least 1 day")
      .max(365, "Extension cannot exceed 365 days")
      .optional(),
  })
  .refine(
    (v) =>
      v.name !== undefined ||
      v.description !== undefined ||
      v.extend_expires_in_days !== undefined,
    { message: "At least one field must be changed" },
  );
export type UpdateAutomationAccountInput = z.infer<
  typeof updateAutomationAccountSchema
>;
